import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {BaseRange, createEditor, Descendant, Editor, NodeEntry, Range, Text, Transforms} from "slate";
import {Editable, ReactEditor, RenderLeafProps, Slate, withReact} from "slate-react";
import {withHistory} from "slate-history";
import {FiFileText, FiFolder, FiSend, FiTag, FiX, FiUsers} from "react-icons/fi";
import {PiBuildings, PiMapPin, PiUser, PiPackage, PiHash, PiCalendarBlank} from "react-icons/pi";
import {createPortal} from "react-dom";

import {ChatContext, ChatContextType, ChatInputState, ChatListItem, SendChatPayload,} from "../../../schemas/chat";
import {COMBINED_FOLDER_ID} from "../../../schemas";
import {ENABLE_ENTITY_TAGS} from "../../../feature_flags";
import {EntityType} from "../../../routines/schemas";
import {ButtonColors} from "../../../colors";
import {useTagContext} from "../../../contexts/tag_context";
import {useFolderContext} from "../../../contexts/folder_context";
import {useEnhancedDocumentsContext} from "../../../contexts/doc_contexts";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";

interface ChatInputProps {
    currentChat: ChatListItem;
    sendMessage: (input: SendChatPayload) => void;
    disabled?: boolean;
}

type PillType = ChatContextType;

type PillSuggestion = {
    type: PillType;
    id?: string;
    label: string;
    entityType?: EntityType;
};

type SuggestionGroup = {
    label: string;
    items: PillSuggestion[];
};

type Pill = {
    type: PillType;
    id?: string;
    label: string;
};

declare module "slate" {
    interface CustomTypes {
        Element: { type: "paragraph"; children: { text: string }[] };
        Text: { text: string };
    }
}

// simple hook for localStorage-backed state
function useLocalStorageState<T>(
    key: string,
    initial: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => {
        try {
            const saved = localStorage.getItem(key);
            return saved ? JSON.parse(saved) : initial;
        } catch {
            return initial;
        }
    });

    useEffect(() => {
        try {
            const saved = localStorage.getItem(key);
            setValue(saved ? JSON.parse(saved) : initial);
        } catch {
            setValue(initial);
        }
    }, [key]);

    const setAndStore: typeof setValue = (updater) => {
        setValue((prev) => {
            const next =
                typeof updater === "function"
                    ? (updater as (p: T) => T)(prev)
                    : updater;
            try {
                localStorage.setItem(key, JSON.stringify(next));
            } catch {
                // ignore errors
            }
            return next;
        });
    };

    return [value, setAndStore];
}

const emptyParagraph: Descendant[] = [
    {type: "paragraph", children: [{text: ""}]},
];

const pillKey = (p: Pill) => `${p.type}:${p.id ?? p.label}`;

// ── Bridge: reads current doc from context into a ref (no state, no re-renders on parent) ──

const DocContextReader: React.FC<{
    docRef: React.MutableRefObject<Pill | null>;
}> = React.memo(({docRef}) => {
    const {document: activeDocument} = useEnhancedDocumentsContext()!;
    docRef.current = activeDocument
        ? {
            type: ChatContextType.FOCUS,  // viewing, not explicitly @'d
            id: activeDocument.id,
            label: activeDocument.title ?? activeDocument.file_name ?? activeDocument.id,
        }
        : null;
    return null;
});
DocContextReader.displayName = 'DocContextReader';

// ── Passive pill: self-contained, subscribes to context, never causes parent re-render ──
// Renders an invisible placeholder of the same size when no doc is active,
// so parent layout never shifts.

const PassivePill: React.FC = React.memo(() => {
    const {document: activeDocument} = useEnhancedDocumentsContext()!;
    const label = activeDocument?.title ?? activeDocument?.file_name ?? activeDocument?.id;

    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] border ${
                label
                    ? "bg-slate-100 text-slate-500 border-slate-200"
                    : "border-transparent"
            }`}
        >
            <FiFileText className={`w-3 h-3 ${label ? "text-slate-400" : "invisible"}`}/>
            <span className={label ? "text-slate-400" : "invisible"}>Viewing:</span>
            <span className={`truncate max-w-[200px] ${label ? "" : "invisible"}`}>
                {label || "\u00A0"}
            </span>
        </span>
    );
});
PassivePill.displayName = 'PassivePill';

// ── Bridge: syncs selected docs → pills without re-rendering ChatInput ──

const DocSelectionBridge: React.FC<{
    onDocsSelected: (pills: Pill[]) => void;
}> = React.memo(({onDocsSelected}) => {
    const {selectedDocIds, loadedDocMap, setSelectedDocIds} = useEnhancedDocumentsContext()!;
    const {setDocSelectedListenersActive} = useNavigationContext()!;

    useEffect(() => {
        setDocSelectedListenersActive(true);
    }, []);

    useEffect(() => {
        if (!selectedDocIds || selectedDocIds.length === 0) return;
        const newPills: Pill[] = selectedDocIds.map(id => ({
            type: ChatContextType.DOCUMENT,
            id,
            label: loadedDocMap.get(id)?.title ?? id,
        }));
        onDocsSelected(newPills);
        setSelectedDocIds([]);
    }, [selectedDocIds, loadedDocMap]);

    return null;
});
DocSelectionBridge.displayName = 'DocSelectionBridge';

// ── Stable Slate wrapper ──

const StableSlateEditor = React.memo<{
    slateKey: number;
    editor: ReactEditor;
    initialValue: Descendant[];
    onChange: (value: Descendant[]) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    disabled: boolean;
    decorate?: (entry: NodeEntry) => BaseRange[];
    renderLeaf?: (props: RenderLeafProps) => React.JSX.Element;
}>(({slateKey, editor, initialValue, onChange, onKeyDown, disabled, decorate, renderLeaf}) => (
    <Slate key={slateKey} editor={editor} initialValue={initialValue} onChange={onChange}>
        <Editable
            placeholder={disabled ? "Chat disabled" : "Type a message to the assistant..."}
            readOnly={disabled}
            className="min-h-[60px] outline-none text-sm text-left"
            onKeyDown={onKeyDown}
            decorate={decorate}
            renderLeaf={renderLeaf}
        />
    </Slate>
));
StableSlateEditor.displayName = 'StableSlateEditor';

const ChatInput: React.FC<ChatInputProps> = React.memo(({
                                                 currentChat,
                                                 sendMessage,
                                                 disabled = false,
                                             }) => {
    const editor = useMemo(
        () => withHistory(withReact(createEditor() as ReactEditor) as ReactEditor),
        []
    );

    const DRAFT_KEY = `chatDraft:${currentChat.id}`;
    const PILLS_KEY = `chatPills:${currentChat.id}`;

    const [value, setValue] = useLocalStorageState<Descendant[]>(
        DRAFT_KEY,
        emptyParagraph
    );
    const [contextPills, setContextPills] = useLocalStorageState<Pill[]>(
        PILLS_KEY,
        []
    );

    const [slateKey, setSlateKey] = useState(() => Date.now());
    const [target, setTarget] = useState<Range | null>(null);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<PillSuggestion[]>([]);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
        top: 0,
        left: 0,
    });

    const containerRef = useRef<HTMLDivElement>(null);

    const {codeTags, entityTags} = useTagContext()!;
    const {groupFolders} = useFolderContext()!;
    const {loadedDocMap: suggestDocMap} = useEnhancedDocumentsContext()!;

    // Ref always holds the current doc pill (written by DocContextReader)
    const currentDocRef = useRef<Pill | null>(null);

    // On keystroke: promote current doc into contextPills if not already there
    const lockCurrentDoc = useCallback(() => {
        const docPill = currentDocRef.current;
        if (!docPill || !docPill.id) return;
        setContextPills((prev) => {
            const key = pillKey(docPill);
            if (prev.some(p => pillKey(p) === key)) return prev;
            return [...prev, docPill];
        });
    }, []);
    const lockCurrentDocRef = useRef(lockCurrentDoc);
    lockCurrentDocRef.current = lockCurrentDoc;

    // Bridge callback: merge selected docs into pills (called by DocSelectionBridge)
    const handleDocsSelected = useCallback((newPills: Pill[]) => {
        setContextPills((prev) => {
            const seen = new Set(prev.map(pillKey));
            const merged = [...prev];
            let changed = false;
            for (const p of newPills) {
                const key = pillKey(p);
                if (!seen.has(key)) {
                    merged.push(p);
                    seen.add(key);
                    changed = true;
                }
            }
            return changed ? merged : prev;
        });
    }, []);

    // reset Slate when tab changes
    useEffect(() => {
        setSlateKey(Date.now());
    }, [currentChat.id]);

    useEffect(() => {
        if (
            Array.isArray(editor.children) &&
            editor.children.length === 0
        ) {
            editor.children = emptyParagraph;
        }
    }, [editor]);

    useEffect(() => {
        if (!target) {
            setSuggestions([]);
            return;
        }
        const q = query.toLowerCase();
        const matches = (name: string) => {
            if (!q) return true;
            const lower = name.toLowerCase();
            const queryWords = q.split(/\s+/).filter(Boolean);
            return queryWords.every(w => lower.includes(w));
        };
        const tagMatches: PillSuggestion[] = codeTags
            .filter((t) => matches(t.tag_name))
            .map((t) => ({type: ChatContextType.TAG, label: t.tag_name}));
        const entityMatches: PillSuggestion[] = ENABLE_ENTITY_TAGS
            ? entityTags
                .filter((t) => matches(t.tag_name))
                .map((t) => ({type: ChatContextType.ENTITY, id: t.id, label: t.tag_name, entityType: t.entity_type}))
            : [];
        const folderMatches: PillSuggestion[] =
            groupFolders
                ?.filter((f) => f.id !== COMBINED_FOLDER_ID && matches(f.tag_name))
                .map((f) => ({type: ChatContextType.FOLDER, id: f.id, label: f.tag_name})) ||
            [];
        const docMatches: PillSuggestion[] = Array.from(suggestDocMap.values())
            .filter((d) => {
                const title = d.title ?? '';
                const fileName = d.file_name ?? '';
                return matches(title) || matches(fileName);
            })
            .slice(0, 15)
            .map((d) => ({
                type: ChatContextType.DOCUMENT,
                id: d.id,
                label: d.file_name
                    ? d.title ? `${d.file_name} (${d.title})` : d.file_name
                    : d.title ?? d.id,
            }));
        setSuggestions([...folderMatches, ...tagMatches, ...docMatches, ...entityMatches]);
        setHighlightIndex(0);
    }, [query, target, codeTags, entityTags, groupFolders, suggestDocMap]);

    // Group suggestions by category for organized rendering
    const suggestionGroups = useMemo((): SuggestionGroup[] => {
        const groups: SuggestionGroup[] = [];
        const folders = suggestions.filter(s => s.type === ChatContextType.FOLDER);
        const tags = suggestions.filter(s => s.type === ChatContextType.TAG);
        const docs = suggestions.filter(s => s.type === ChatContextType.DOCUMENT);
        const entities = suggestions.filter(s => s.type === ChatContextType.ENTITY);

        if (folders.length) groups.push({label: 'Folders', items: folders});
        if (tags.length) groups.push({label: 'Codes', items: tags});
        if (docs.length) groups.push({label: 'Documents', items: docs});

        // Group entities by type
        if (entities.length) {
            const byType = new Map<EntityType | undefined, PillSuggestion[]>();
            for (const e of entities) {
                const key = e.entityType;
                if (!byType.has(key)) byType.set(key, []);
                byType.get(key)!.push(e);
            }
            const typeOrder: (EntityType | undefined)[] = [
                EntityType.person, EntityType.organization, EntityType.location,
                EntityType.item, EntityType.topic, EntityType.event, undefined,
            ];
            const typeLabels: Record<string, string> = {
                [EntityType.person]: 'People',
                [EntityType.organization]: 'Organizations',
                [EntityType.location]: 'Locations',
                [EntityType.item]: 'Items',
                [EntityType.topic]: 'Topics',
                [EntityType.event]: 'Events',
            };
            for (const t of typeOrder) {
                const items = byType.get(t);
                if (items?.length) {
                    groups.push({label: typeLabels[t as string] ?? 'Entities', items});
                }
            }
        }
        return groups;
    }, [suggestions]);

    useEffect(() => {
        if (!target) return;
        try {
            const domRange = ReactEditor.toDOMRange(editor, target);
            const rect = domRange.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
            });
        } catch {
            return;
        }
    }, [editor, target, value]);

    const onChange = useCallback((newValue: Descendant[]) => {
        setValue(newValue);

        // Lock current doc on keystroke (if editor has content)
        const text = Editor.string(editor, []);
        if (text.trim().length > 0) {
            lockCurrentDocRef.current();
        }

        // Sync: if an @mention was deleted from text, remove the matching pill.
        // Only applies to pills added via @mention (tracked in mentionPillKeys).
        // Pills added via the "Add to context" button are never removed by typing.
        const lowerText = text.toLowerCase();
        setContextPills(prev => {
            const kept = prev.filter(p => {
                const key = pillKey(p);
                if (!mentionPillKeysRef.current.has(key)) return true; // not from @mention
                return lowerText.includes(`@${p.label.toLowerCase()}`);
            });
            return kept.length === prev.length ? prev : kept;
        });

        const {selection} = editor;
        if (selection && Range.isCollapsed(selection)) {
            const before = Editor.before(editor, selection.anchor, {
                unit: "character",
                distance: 50,
            });
            const beforeRange = before && Editor.range(editor, before, selection.anchor);
            const beforeText = beforeRange ? Editor.string(editor, beforeRange) : "";
            // Allow spaces in @mentions so multi-word names work (e.g., "@Dr. Roth")
            // Stops at a second @ or newline, not at spaces
            const match = beforeText.match(/@([^@\n]*)$/);
            if (match) {
                const mentionText = (match[1] ?? "").trimEnd();
                // Don't reopen the menu if this @mention is already a pill
                if (!pillLabelsRef.current.has(mentionText.toLowerCase())) {
                    const matchLength = match[0].length;
                    const start = Editor.before(editor, selection.anchor, {
                        unit: "character",
                        distance: matchLength,
                    });
                    if (start) {
                        const range = Editor.range(editor, start, selection.anchor);
                        setTarget(range);
                        setQuery(match[1] ?? "");
                        return;
                    }
                }
            }
        }
        setTarget(null);
        setQuery("");
    }, [editor]);

    const addPill = (p: Pill) => {
        setContextPills((prev) => {
            // If this doc exists as FOCUS, promote it to DOCUMENT (explicit @)
            const existingFocus = prev.find(
                existing => existing.id === p.id && existing.type === ChatContextType.FOCUS
            );
            if (existingFocus) {
                return prev.map(existing =>
                    existing.id === p.id && existing.type === ChatContextType.FOCUS
                        ? {...existing, type: ChatContextType.DOCUMENT}
                        : existing
                );
            }
            const seen = new Set(prev.map(pillKey));
            if (seen.has(pillKey(p))) return prev;
            return [...prev, p];
        });
    };

    const removePill = (key: string) => {
        setContextPills((prev) => prev.filter((p) => pillKey(p) !== key));
    };

    // Track pill labels for the decorator and text-deletion sync
    const pillLabelsRef = useRef<Set<string>>(new Set());
    // Track which pills were added via @mention (vs button/bridge)
    const mentionPillKeysRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        pillLabelsRef.current = new Set(contextPills.map(p => p.label.toLowerCase()));
    }, [contextPills]);

    // Decorator: highlight @mentions that match a pill in faint style
    const decorate = useCallback(([node, path]: NodeEntry): BaseRange[] => {
        const ranges: BaseRange[] = [];
        if (!Text.isText(node)) return ranges;
        const text = node.text;
        const re = /@([^\n]+?)(?=\s@|\s*$)/g;
        let m;
        while ((m = re.exec(text)) !== null) {
            if (pillLabelsRef.current.has(m[1].toLowerCase())) {
                ranges.push({
                    anchor: {path, offset: m.index},
                    focus: {path, offset: m.index + m[0].length},
                    mention: true,
                } as BaseRange & { mention: boolean });
            }
        }
        return ranges;
    }, []);

    // Render leaves — faint style for @mentions
    const renderLeaf = useCallback((props: RenderLeafProps) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isMention = (props.leaf as any).mention;
        return (
            <span {...props.attributes} className={isMention ? 'text-indigo-300' : undefined}>
                {props.children}
            </span>
        );
    }, []);

    const onKeyDownImpl = (e: React.KeyboardEvent) => {
        if (target && suggestions.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIndex((i) => (i + 1) % suggestions.length);
                return;
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
                return;
            } else if (e.key === "Enter") {
                e.preventDefault();
                const s = suggestions[highlightIndex];
                const pill = {type: s.type, id: s.id, label: s.label};
                addPill(pill);
                mentionPillKeysRef.current.add(pillKey(pill));
                if (target) {
                    Transforms.select(editor, target);
                    Transforms.insertText(editor, `@${s.label} `);
                }
                setTarget(null);
                setQuery("");
                setSuggestions([]);
                return;
            } else if (e.key === "Escape") {
                e.preventDefault();
                setTarget(null);
                setQuery("");
                setSuggestions([]);
                return;
            }
        }
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    const onKeyDownRef = useRef(onKeyDownImpl);
    onKeyDownRef.current = onKeyDownImpl;
    const onKeyDown = useCallback((e: React.KeyboardEvent) => onKeyDownRef.current(e), []);

    const handleClickSuggestion = (s: PillSuggestion) => {
        const pill = {type: s.type, id: s.id, label: s.label};
        addPill(pill);
        mentionPillKeysRef.current.add(pillKey(pill));
        if (target) {
            Transforms.select(editor, target);
            Transforms.insertText(editor, `@${s.label} `);
        }
        setTarget(null);
        setQuery("");
        setSuggestions([]);
    };

    const serialize = (nodes: Descendant[]): string =>
        nodes
            .map((n) => {
                if ("text" in n) return n.text;
                if ("children" in n && Array.isArray(n.children)) return serialize(n.children);
                return "";
            })
            .join("");

    const clearPills = () => {
        setContextPills([]);
        localStorage.removeItem(PILLS_KEY);
    };

    const clearAllInput = () => {
        try {
            Transforms.delete(editor, {
                at: {
                    anchor: Editor.start(editor, []),
                    focus: Editor.end(editor, []),
                },
            });
            Transforms.select(editor, Editor.start(editor, []));
        } catch {
            // editor in bad state, just reset
        }

        setValue(emptyParagraph);
        localStorage.removeItem(DRAFT_KEY);
        clearPills();
        setSlateKey(Date.now());
    };

    const handleSend = () => {
        if (disabled) return;
        const text = serialize(value).trim();
        if (!text) return;
        // If no explicit pills, fall back to current doc from ref
        const allPills = contextPills.length > 0
            ? contextPills
            : currentDocRef.current ? [currentDocRef.current] : [];
        const chatContexts: ChatContext[] = allPills
            .filter((p): p is Pill & { id: string } => Boolean(p.id))
            .map((p) => ({id: p.id!, type: p.type as ChatContextType}));

        // Append context summary for pills not already @mentioned in the text
        const textLower = text.toLowerCase();
        const unmentioned = allPills.filter(p =>
            p.label && !textLower.includes(`@${p.label.toLowerCase()}`)
        );
        let finalText = text;
        if (unmentioned.length > 0) {
            const labels = unmentioned.map(p => {
                const icon = p.type === ChatContextType.FOLDER ? '📁'
                    : p.type === ChatContextType.TAG ? '🏷️'
                    : p.type === ChatContextType.ENTITY ? '👤'
                    : '📄';
                return `${icon} ${p.label}`;
            });
            finalText = `${text}\n\n*Context: ${labels.join(', ')}*`;
        }

        sendMessage({
            input: finalText,
            inputState: ChatInputState.ANY_TEXT,
            chatContexts,
        });
        clearAllInput();
    };

    return (
        <div className="p-4 pt-2 border-t bg-white relative">
            {/* Context row — min height keeps layout stable, grows for multiple pills */}
            <div className="flex flex-wrap gap-1.5 mb-1 min-h-[26px] items-center">
                {contextPills.length > 0 ? (
                    <>
                        {contextPills.map((p) => {
                            const key = pillKey(p);
                            return (
                                <span
                                    key={key}
                                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${
                                        p.type === ChatContextType.FOCUS
                                            ? "bg-slate-50 text-slate-400 border-slate-200"
                                            : "bg-indigo-50 text-indigo-600 border-indigo-200"
                                    }`}
                                >
                                    {p.type === ChatContextType.TAG && <FiTag className="w-3.5 h-3.5"/>}
                                    {p.type === ChatContextType.FOLDER && <FiFolder className="w-3.5 h-3.5"/>}
                                    {p.type === ChatContextType.DOCUMENT && <FiFileText className="w-3.5 h-3.5"/>}
                                    {p.type === ChatContextType.FOCUS && <FiFileText className="w-3.5 h-3.5"/>}
                                    {p.type === ChatContextType.ENTITY && <FiUsers className="w-3.5 h-3.5"/>}
                                    <span className="truncate max-w-[180px]">{p.label}</span>
                                    <button
                                        type="button"
                                        className="p-0.5 hover:bg-indigo-100 rounded-full"
                                        onClick={() => removePill(key)}
                                        aria-label="Remove"
                                    >
                                        <FiX className="w-3 h-3"/>
                                    </button>
                                </span>
                            );
                        })}
                        {contextPills.length > 1 && (
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-red-500 hover:bg-red-50 transition-colors"
                                onClick={clearPills}
                            >
                                Clear all
                                <FiX className="w-3 h-3"/>
                            </button>
                        )}
                    </>
                ) : (
                    <PassivePill/>
                )}
            </div>

            {/* Invisible bridges */}
            <DocContextReader docRef={currentDocRef}/>
            <DocSelectionBridge onDocsSelected={handleDocsSelected}/>

            <div
                ref={containerRef}
                className={`flex-grow border rounded-2xl px-4 py-2 text-sm shadow-sm relative ${
                    disabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700"
                }`}
            >
                <StableSlateEditor
                    slateKey={slateKey}
                    editor={editor}
                    initialValue={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    decorate={decorate}
                    renderLeaf={renderLeaf}
                />

                {target &&
                    suggestions.length > 0 &&
                    createPortal(
                        <div
                            className="fixed z-[9999]"
                            style={{
                                bottom: `${window.innerHeight - menuPos.top + 8}px`,
                                left: Math.min(menuPos.left, window.innerWidth - 320),
                            }}
                        >
                            <ul className="w-[320px] bg-white border border-slate-200 shadow-lg rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                                {suggestionGroups.map((group) => {
                                    // Compute flat index offset for keyboard highlight
                                    let flatOffset = 0;
                                    for (const g of suggestionGroups) {
                                        if (g === group) break;
                                        flatOffset += g.items.length;
                                    }
                                    return (
                                        <React.Fragment key={group.label}>
                                            <li className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">
                                                {group.label}
                                            </li>
                                            {group.items.map((s, gi) => {
                                                const flatIdx = flatOffset + gi;
                                                const entityIcon = s.entityType ? ({
                                                    [EntityType.person]: <PiUser className="w-3.5 h-3.5 text-indigo-500"/>,
                                                    [EntityType.organization]: <PiBuildings className="w-3.5 h-3.5 text-emerald-500"/>,
                                                    [EntityType.location]: <PiMapPin className="w-3.5 h-3.5 text-amber-500"/>,
                                                    [EntityType.item]: <PiPackage className="w-3.5 h-3.5 text-violet-500"/>,
                                                    [EntityType.topic]: <PiHash className="w-3.5 h-3.5 text-cyan-500"/>,
                                                    [EntityType.event]: <PiCalendarBlank className="w-3.5 h-3.5 text-slate-500"/>,
                                                })[s.entityType] : null;
                                                return (
                                                    <li
                                                        key={`${s.type}-${s.id ?? s.label}`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            handleClickSuggestion(s);
                                                        }}
                                                        className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer text-sm transition-colors ${
                                                            flatIdx === highlightIndex
                                                                ? 'bg-indigo-50 text-indigo-700'
                                                                : 'text-slate-700 hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <span className={`shrink-0 ${flatIdx === highlightIndex ? 'text-indigo-500' : 'text-slate-400'}`}>
                                                            {entityIcon
                                                                ?? (s.type === ChatContextType.TAG ? <FiTag className="w-3.5 h-3.5"/>
                                                                : s.type === ChatContextType.FOLDER ? <FiFolder className="w-3.5 h-3.5"/>
                                                                : s.type === ChatContextType.DOCUMENT ? <FiFileText className="w-3.5 h-3.5"/>
                                                                : <FiUsers className="w-3.5 h-3.5"/>)}
                                                        </span>
                                                        <span className="truncate">{s.label}</span>
                                                    </li>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </ul>
                        </div>,
                        window.document.body
                    )}
            </div>

            <div className="absolute left-2 bottom-1 text-xs text-slate-400 pointer-events-none">
                Type <span className="font-semibold">@</span> to add context pills, or
                select documents in explorer.
            </div>

            <div className="flex justify-end mt-2">
                <button
                    type="button"
                    onClick={handleSend}
                    disabled={disabled}
                    className={`rounded px-3 py-2 shadow-sm transition-all flex items-center justify-center ${
                        disabled
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : ButtonColors.dark + ButtonColors.hoverDark + " text-white"
                    }`}
                >
                    <FiSend className="w-5 h-5"/>
                </button>
            </div>
        </div>
    );
});
ChatInput.displayName = 'ChatInput';

export default ChatInput;
